/* eslint-disable no-else-return, no-unused-vars */

import { getCountries, getCountryData } from '@gameday/common/functions'
import { withStyles } from '@material-ui/core/styles'
import ArrowDropDown from '@material-ui/icons/ArrowDropDown'
import cn from 'classnames'
import React from 'react'
import Select, { components } from 'react-select'

import theme from '../../utils/theme'
import LabelWrapper from '../LabelWrapper'

const { Option } = components

const styles = theme => ({
  countrySelect: {
    '& .wrapped:not(.bordered)': {
      borderBottom: theme.borders.default,
      '& .country-select__control': {
        border: 'none',
      },
      '& .country-select-wrapper:hover .country-select__control, & .country-select-wrapper:focus-within .country-select__control':
        {
          backgroundColor: `var(--gameday-inputField-inputBGHover, ${theme.inputField.inputBGHover})`,
        },
    },
    '& .wrapped:not(.full-width) .country-select-wrapper': {
      maxWidth: '280px',
      width: '100%',
    },
    '&:focus-within .wrapped:not(.bordered)': {
      borderColor: theme.borders.borderColorActive,
    },
    '& .country-select__control': {
      height: '36px',
      minHeight: 'unset',
      '&.country-select__control--is-focused': {
        borderColor: theme.borders.borderColorActive,
        boxShadow: 'none',
        alignItems: 'flex-start',
      },
      '&.country-select__control--is-disabled': {
        backgroundColor: `${`var(--gameday-inputField-inputBGDisabled, ${theme.inputField.inputBGDisabled})`} !important`,
        borderColor: 'rgb(204, 204, 204)',
      },
    },
    '& .country-select-wrapper': {
      width: props => props.customWidth && props.customWidth,
      '&:hover *': {
        cursor: props => (props.isSearchable ? null : 'pointer'),
        backgroundColor: `var(--gameday-inputField-inputBGHover, ${theme.inputField.inputBGHover})`,
      },
    },
    '& .country-select__menu': {
      minWidth: '200px',
    },
    '& .country-select__value-container': {
      height: '34px',
      padding: '0 0 0 0.625rem',
      fontSize: theme.typography.fontSizeSm,
      flexWrap: 'nowrap',
      '&  > div:last-child': {
        margin: 0,
        padding: 0,
      },
      '&:hover': {
        cursor: 'pointer',
        '& .country-select__placeholder': {
          color: `var(--gameday-inputField-inputTextColor, ${theme.inputField.inputTextColor})`,
        },
      },
      '&:focus-within .country-select__single-value': {
        fontWeight: theme.typography.fontWeightBolder,
      },
    },
    '& .country-select__placeholder': {
      color: theme.inputField.inputPlaceholderColor,
      '&:hover': {
        color: `var(--gameday-inputField-inputTextColor, ${theme.inputField.inputTextColor})`,
      },
    },
    '& .country-select__input input': {
      height: '34px',
    },
    '& .country-select__indicators': {
      padding: '0 2px',
      '& .country-select__indicator': {
        padding: 0,
      },
    },
    '& .country-select__indicator-separator': {
      display: 'none',
    },
    '& .country-select__option': {
      color: `var(--gameday-inputField-inputTextColor, ${theme.inputField.inputTextColor})`,
      display: 'flex',
      '&.country-select__option--is-selected': {
        backgroundColor: '#dbdbdb',
        color: `var(--gameday-inputField-inputTextColor, ${theme.inputField.inputTextColor})`,
      },
      '&.country-select__option--is-focused': {
        backgroundColor: `${`var(--gameday-inputField-inputBGDisabled, ${theme.inputField.inputBGDisabled})`}`,
      },
    },
    '& .has-error': {
      '& .country-select__control, & $singleInput': {
        borderColor: theme.status.errorColor,
        backgroundColor: theme.inputField.inputBGError,
      },
    },
  },
  countrySelectControl: {
    '& .country-select__control, & .country-select__control:hover': {
      borderColor: `var(--gameday-borders-borderColor, ${theme.borders.borderColor})`,
      backgroundColor: `var(--gameday-inputField-countrySelectBg, ${theme.inputField.countrySelectBg})`,
    },
  },
  arrow: {
    color: theme.inputField.iconColor,
    backgroundColor: 'transparent',
  },
  customRow: {
    display: 'flex !important',
    alignItems: 'center',
    flexWrap: 'nowrap',
    '&.country-select__option': {
      color: `var(--gameday-inputField-inputTextColor, ${theme.inputField.inputTextColor})`,
    },
    '&.country-select__option--is-selected': {
      backgroundColor: `var(--gameday-inputField-inputBGHover, ${theme.inputField.inputBGHover})`,
    },
    '&.country-select__option--is-focused': {
      backgroundColor: `var(--gameday-inputField-inputBGHover, ${theme.inputField.inputBGHover})`,
    },
  },
  customOption: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  emoji: {
    marginRight: '8px',
  },
  value: {
    display: 'contents',
    whiteSpace: 'nowrap',
    height: '36px',
    color: `var(--gameday-inputField-inputTextColor, ${theme.inputField.inputTextColor})`,
    fontSize: props => props.countryLabelType === 'emoji' && '32px',
  },
})

const customDropDownStyles = hierarchyCountryCode => ({
  menuPortal: base => ({
    ...base,
    pointerEvents: 'all',
    zIndex: 999999,
    minWidth: '280px',
  }),
  menu: base => ({
    ...base,
    backgroundColor: `var(--gameday-inputField-inputBGColor, ${theme.inputField.inputBGColor})`,
  }),
  option: (provided, { data }) => ({
    ...provided,
    ...(hierarchyCountryCode &&
      data.code === hierarchyCountryCode && {
        marginBottom: '4px',
        position: 'relative',
        paddingBottom: '12px',
        backgroundImage:
          'linear-gradient(to right, transparent 8px, #e0e0e0 8px, #e0e0e0 calc(100% - 8px), transparent calc(100% - 8px))',
        backgroundPosition: 'bottom',
        backgroundSize: '100% 1px',
        backgroundRepeat: 'no-repeat',
      }),
  }),
})

class CountrySelect extends React.Component {
  state = {
    selectedOption: null,
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (
      nextProps.defaultCountry !== (prevState.selectedOption ? prevState.selectedOption.label : '') ||
      (nextProps.value && nextProps.value.label !== prevState.selectedOption ? prevState.selectedOption.label : '')
    ) {
      return {
        selectedOption: getCountryData(nextProps.value),
      }
    } else return ''
  }

  handleChange = selectedOption => {
    const { onChange } = this.props
    this.setState({ selectedOption })
    onChange({ selectedOption })
  }

  render() {
    const {
      children,
      classes,
      className,
      countries,
      countryFilterType,
      countryLabelType,
      countrySortBy,
      customCol,
      customMargin,
      customPadding,
      customWidth,
      defaultCountry,
      disabled,
      errorText,
      fieldLabel,
      fullWidth,
      handleClear,
      helperText,
      hierarchyCountryCode,
      id,
      isClearable,
      isLabelInline,
      isRequired,
      isSearchable,
      isToolTip,
      justify,
      label,
      labelIcon,
      menuIsOpen,
      multipleFields,
      noLabel,
      noMobile,
      notWrapper,
      noWrap,
      placeholder,
      showClear,
      spacing,
      toolTipIcon,
      toolTipPosition,
      toolTipText,
      // biome-ignore lint/correctness/noUnusedVariables: TODO
      value,
      variant,
      withBorder,
      withDivider,
      withFlags,
    } = this.props

    const { selectedOption } = this.state

    const DownArrow = () => <ArrowDropDown className={classes.arrow} />

    const labelType = (countryLabelType, data) => {
      return data[countryLabelType]
    }

    // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
    const SingleValue = ({ data, selectProps }) => {
      return (
        <div className={classes.customRow}>
          {withFlags && data.emoji && countryLabelType !== 'emoji' && (
            <div className={cn([classes.emoji], 'country-emoji')}>{data.emoji}</div>
          )}
          <div className={cn([classes.value], 'country-selected-value')}>
            {countryLabelType === 'phone' && '+'}
            {labelType(countryLabelType, data)}
          </div>
        </div>
      )
    }

    const IconOption = props => (
      <Option className={cn(classes.customRow, classes.customOption)} {...props}>
        {withFlags && props.data.emoji && (
          <div className={cn([classes.emoji], 'country-emoji')}>{props.data.emoji}</div>
        )}
        <div className={classes.option}>
          {props.data.label} {countryLabelType === 'phone' && ` (+${props.data.phone})`}
        </div>
      </Option>
    )

    if (noLabel) {
      return (
        <Select
          className={cn(className, 'country-select-wrapper', classes.countrySelectControl)}
          classNamePrefix="country-select"
          value={selectedOption || getCountryData(defaultCountry)}
          onChange={this.handleChange}
          options={getCountries(countries, countryFilterType, countrySortBy, hierarchyCountryCode)}
          placeholder={placeholder}
          components={{
            DropdownIndicator: DownArrow,
            Option: IconOption,
            SingleValue,
          }}
          isClearable={isClearable}
          isDisabled={disabled}
          isSearchable={isSearchable}
          styles={customDropDownStyles(hierarchyCountryCode)}
          menuPlacement="bottom"
          menuPortalTarget={document.body}
          menuPosition="absolute"
          menuIsOpen={menuIsOpen}
          id={`${id}-country-select`}
        />
      )
    }
    return (
      <LabelWrapper
        className={cn([classes.countrySelect], className)}
        customCol={customCol}
        customMargin={customMargin}
        customPadding={customPadding}
        customWidth={customWidth}
        disabled={disabled}
        errorText={errorText}
        fieldLabel={fieldLabel}
        fullWidth={fullWidth}
        handleClear={handleClear}
        helperText={helperText}
        id={id}
        isLabelInline={isLabelInline}
        isRequired={isRequired}
        isToolTip={isToolTip}
        justify={justify}
        label={label}
        labelIcon={labelIcon}
        multipleFields={multipleFields}
        noMobile={noMobile}
        notWrapper={notWrapper}
        noWrap={noWrap}
        showClear={showClear}
        spacing={spacing}
        toolTipIcon={toolTipIcon}
        toolTipPosition={toolTipPosition}
        toolTipText={toolTipText}
        variant={variant}
        withBorder={withBorder}
        withDivider={withDivider}
      >
        <Select
          className={cn('country-select-wrapper', classes.countrySelectControl)}
          classNamePrefix="country-select"
          value={selectedOption || getCountryData(defaultCountry)}
          onChange={this.handleChange}
          options={getCountries(countries, countryFilterType, countrySortBy, hierarchyCountryCode)}
          placeholder={placeholder}
          components={{
            DropdownIndicator: DownArrow,
            Option: IconOption,
            SingleValue,
          }}
          isClearable={isClearable}
          isDisabled={disabled}
          isSearchable={isSearchable}
          styles={customDropDownStyles(hierarchyCountryCode)}
          menuPlacement="bottom"
          menuPortalTarget={document.body}
          menuPosition="absolute"
          menuIsOpen={menuIsOpen}
          id={`${id}-country-select`}
        />
        {children}
      </LabelWrapper>
    )
  }
}

CountrySelect.defaultProps = {
  countries: null,
  countryFilterType: 'include',
  countryLabelType: 'name',
  countrySortBy: 'label',
  defaultCountry: null,
  isSearchable: true,
  placeholder: 'Select a country',
  multipleFields: false,
  withFlags: true,
}

const CountrySelectComponent = withStyles(styles)(CountrySelect)
CountrySelectComponent.displayName = 'CountrySelect'
export default CountrySelectComponent
